// Lightweight types + utilities split out of the heavy templates.ts (437KB)
// so components/hooks can import these without pulling the full templates array.

export type ToolKey =
  | "n8n" | "openai" | "zapier" | "make" | "notion" | "sheets"
  | "gmail" | "slack" | "discord" | "telegram" | "twilio" | "stripe"
  | "webhooks" | "github" | "whatsapp" | "airtable" | "drive";

export type Template = {
  id: number;
  slug: string;
  name: string;
  title: string;
  description: string;
  tags: string[];
  tools: ToolKey[];
  youtubeUrl: string;
  youtubeId: string;
  thumbnail: string;
  downloadUrl?: string;
  skoolUrl?: string;
  related: string[];
};

export const toolIcons: Record<ToolKey, string> = {
  n8n: "",
  openai: "",
  zapier: "",
  make: "",
  notion: "",
  sheets: "",
  gmail: "",
  slack: "",
  discord: "",
  telegram: "",
  twilio: "",
  stripe: "",
  webhooks: "",
  github: "",
  whatsapp: "",
  airtable: "",
  drive: "",
};

export function filterTemplates(templates: Template[], searchQuery: string): Template[] {
  if (!searchQuery.trim()) return templates;

  const query = searchQuery.toLowerCase().trim();
  const uniqueIds = new Set<number>();
  const titleMatches: Template[] = [];
  const descriptionMatches: Template[] = [];

  templates.forEach((template) => {
    if (uniqueIds.has(template.id)) return;

    const title = template.title.toLowerCase();
    const description = template.description.toLowerCase();
    const name = template.name.toLowerCase();
    const tags = template.tags.join(" ").toLowerCase();

    if (title.includes(query) || name.includes(query)) {
      titleMatches.push(template);
      uniqueIds.add(template.id);
    } else if (description.includes(query) || tags.includes(query)) {
      descriptionMatches.push(template);
      uniqueIds.add(template.id);
    }
  });

  return [...titleMatches, ...descriptionMatches];
}

const templateSectionMatchers: Array<{ label: string; matches: (template: Template) => boolean }> = [
  {
    label: "Lead Generation",
    matches: (t) =>
      t.tags.some((tag) => ["lead-generation", "leadgen", "leads", "prospect"].includes(tag.toLowerCase())) ||
      t.name.toLowerCase().includes("lead") ||
      t.description.toLowerCase().includes("lead generation"),
  },
  {
    label: "Customer Support",
    matches: (t) =>
      t.tags.some((tag) => ["customer-support", "support", "helpdesk", "service"].includes(tag.toLowerCase())) ||
      t.name.toLowerCase().includes("support") ||
      t.description.toLowerCase().includes("customer support"),
  },
  {
    label: "E-commerce",
    matches: (t) =>
      t.tags.some((tag) => ["e-commerce", "ecommerce", "sales", "store", "product"].includes(tag.toLowerCase())) ||
      t.name.toLowerCase().includes("ecommerce") ||
      t.description.toLowerCase().includes("e-commerce"),
  },
  {
    label: "Social Media",
    matches: (t) =>
      t.tags.some((tag) => ["social-media", "social", "instagram", "tiktok", "youtube", "facebook", "twitter", "linkedin"].includes(tag.toLowerCase())) ||
      ["social media", "instagram", "tiktok", "youtube", "linkedin"].some((term) => t.description.toLowerCase().includes(term)),
  },
  {
    label: "Data Analysis",
    matches: (t) =>
      t.tags.some((tag) => ["data-analysis", "analytics", "data", "analysis", "insights"].includes(tag.toLowerCase())) ||
      t.name.toLowerCase().includes("analysis") ||
      ["data analysis", "analytics"].some((term) => t.description.toLowerCase().includes(term)),
  },
  {
    label: "Marketing",
    matches: (t) =>
      t.tags.some((tag) => ["marketing", "advertisement", "campaign", "promotion", "content"].includes(tag.toLowerCase())) ||
      t.name.toLowerCase().includes("marketing") ||
      ["marketing", "content creation"].some((term) => t.description.toLowerCase().includes(term)),
  },
];

export function filterTemplatesByTitleOrSection(templates: Template[], searchQuery: string): Template[] {
  const query = searchQuery.toLowerCase().trim();
  if (!query) return templates;

  return templates.filter((template) => {
    const titleMatches = template.title.toLowerCase().includes(query) || template.name.toLowerCase().includes(query);
    const sectionMatches = templateSectionMatchers.some(
      (section) => section.label.toLowerCase().includes(query) && section.matches(template),
    );

    return titleMatches || sectionMatches;
  });
}

export function getRelatedTemplates(
  currentTemplate: Template,
  allTemplates: Template[],
): Template[] {
  const relatedBySlugs = allTemplates.filter(
    (t) => currentTemplate.related.includes(t.slug) && t.id !== currentTemplate.id,
  );

  if (relatedBySlugs.length >= 3) {
    return relatedBySlugs.slice(0, 4);
  }

  const relatedByContent = allTemplates
    .filter((t) => t.id !== currentTemplate.id)
    .map((template) => ({
      template,
      score:
        template.tags.filter((tag) => currentTemplate.tags.includes(tag)).length +
        template.tools.filter((tool) => currentTemplate.tools.includes(tool)).length,
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((item) => item.template);

  const combined = [...relatedBySlugs];
  for (const t of relatedByContent) {
    if (!combined.find((c) => c.id === t.id)) combined.push(t);
    if (combined.length >= 4) break;
  }
  return combined.slice(0, 4);
}
